import {
  SignOutIcon,
  MagnifyingGlassIcon,
  SidebarSimpleIcon,
  ShieldCheckIcon,
  GearIcon,
  SlidersHorizontalIcon,
  CheckIcon,
  BuildingsIcon,
  ProhibitIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useRef } from "react";
import { Sentry } from "~/sentry";
import { useLocation, useNavigate } from "react-router";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { InitialsAvatar } from "~/components/ui/avatar";
import { NotificationBell } from "~/components/notification-bell";
import { useInspector } from "~/lib/stores/ui-store";
import { useBackgroundTasks } from "~/hooks/queries/use-inspector";
import { useChatStatuses } from "~/hooks/queries/use-chat-status";
import { useApprovals } from "~/hooks/queries/use-approvals";
import { useUserDisplayName, useSwitchableTenants, useSwitchTenant } from "~/hooks/queries/use-tenant";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getInspectorTooltip } from "~/components/inspector-indicator";
import { resolvePendingApprovalsAffordance } from "~/components/pending-approvals-affordance";
import { useDesktopComposerHost } from "~/features/chat/use-desktop-composer-host";
import { newChatAsset } from "~/features/chat/modal-asset";
import { useChatOpenStore } from "~/lib/stores/chat-open-store";
import { useOpenSettings, useOpenSystemSettings } from "~/features/settings/use-open-settings";
// `isHomeRoute` is used by the pending-approvals badge's navigate-home step
// (FRONT-1323). It lived in `~/features/inspector/background-tasks` when that
// call was written and moved here under FRONT-1550 — the two landed in the
// same file from different branches, so the merge kept both hunks and lost the
// import. See the note on `openPendingApprovals` below.
import { isHomeRoute, originToReturnTo } from "~/lib/chat-origin";
import {
  useFeatureFlag,
  SHOW_INSPECTOR_FLAG,
} from "~/hooks/queries/use-feature-flags";
import { ChatTopBar } from "~/features/chat/top-bar/chat-top-bar";

/**
 * Top-right toolbar — three zones:
 *   - Live signals (bell, pending-approvals badge, Inspector activity dot).
 *     These need to be glance-visible; they convey "something is happening".
 *   - Command-palette launcher (⌘K) — replaces the standalone Inspector /
 *     Terminal buttons that used to live here.
 *   - Avatar dropdown — User Settings + System Settings + Sign out, anchored
 *     on identity.
 */
export function AppHeader() {
  const { open: inspectorOpen, toggle: toggleInspector, setOpen: setInspectorOpen } = useInspector();
  const navigate = useNavigate();
  const location = useLocation();
  const userName = useUserDisplayName();
  // FRONT-1357: tenant switcher. The backend answers an empty list while
  // grant-based tenancy is off (or the user has no cross-org grants), so the
  // menu section simply does not render in those cases.
  const { data: switchable } = useSwitchableTenants();
  const switchTenant = useSwitchTenant();
  // FRONT-1073/FRONT-1080: lets a new chat start from any tab, not just
  // Inbox. Reviewed on PR #668 (Marko) — an earlier version of this button
  // owned its own InboxChatModal instance, which could end up open
  // alongside whatever modal the current page already manages (Home, Chats,
  // Files, Memory all own one), stacking two full-screen overlays. Routing
  // through the same `useChatOpenStore` + navigate-home mechanism the
  // Inspector's monitored-chats list already uses (FRONT-634) means there is
  // only ever the one modal owner (Home's launcher); FRONT-1076's SWITCH-bar
  // wiring lives there already, so nothing needed duplicating here.
  const requestOpenChat = useChatOpenStore((s) => s.requestOpenChat);
  const openSettings = useOpenSettings();
  const openSystemSettings = useOpenSystemSettings();
  // FRONT-1323: gates the Inspector's own toggle below, alongside the panel
  // itself in app-layout.tsx. Fails open — see SHOW_INSPECTOR_FLAG.
  const inspectorEnabled = useFeatureFlag(SHOW_INSPECTOR_FLAG, true);
  // `inspectorOpen` is PERSISTED (ui-store, FRONT-1187), so a browser that had
  // the panel open before the flag was switched off still reports open for a
  // panel that no longer mounts. Anything describing what the user can SEE has
  // to read this instead, or the approvals badge below styles itself as
  // "panel is showing" against an empty right-hand column.
  const inspectorPanelOpen = inspectorEnabled && inspectorOpen;
  const queryClient = useQueryClient();
  const userEmail = queryClient.getQueryData<{ email?: string }>([
    "auth",
    "me",
  ])?.email;

  const { data: tasks } = useBackgroundTasks();
  const activeTaskCount = tasks?.counts.active ?? 0;
  // FRONT-661: the indicator also reads the chat-status spine, so a chat that is
  // working or needs you lights the dot — the same source the chat pills read.
  const { data: chatStatuses } = useChatStatuses();
  const chatsNeedAttention = chatStatuses
    ? Object.values(chatStatuses).some(
        (s) => s.state === "working" || s.state === "needs-you",
      )
    : false;
  // FRONT-662: a chat paused on a permission/question pulls the user back. When
  // any monitored chat is in needs-you, the indicator dot uses the quicker
  // pull-back pulse instead of the calm working pulse — same amber, faster beat.
  const chatsNeedYou = chatStatuses
    ? Object.values(chatStatuses).some((s) => s.state === "needs-you")
    : false;
  const inspectorActive = activeTaskCount > 0 || chatsNeedAttention;

  const { data: approvals = [] } = useApprovals();
  // FRONT-1323: the badge stays visible regardless of the Inspector flag — it is
  // the only cross-page "something is waiting on you" signal, since AppHeader
  // mounts on every route while every other approvals surface is scoped to the
  // one chat currently open. Only its DESTINATION moves. See
  // `pending-approvals-affordance.ts` for the three cases.
  const approvalsBadge = resolvePendingApprovalsAffordance(
    approvals,
    inspectorEnabled,
  );

  // FRONT-1323: `deadEnd` is the carried dead-end — approvals are
  // pending, the Inspector is gated off, and not one of them has a chat to
  // open, so the badge can only report the count and disable itself. Every
  // producer traced says this should be unreachable now (see the matching note
  // in `approvals-service.ts`), but that is a claim about code, not a
  // measurement. Report it so the question gets settled by data: silence means
  // the `none` branch is dead code and the disabled button can go, a hit names
  // the approvals that need a surface.
  //
  // Keyed on the approval ids, not just the count: this must fire once per
  // distinct set, or it would re-report on every render and on every refetch
  // that returns the same rows.
  const reportedDeadEndKey = useRef<string | null>(null);
  useEffect(() => {
    // FRONT-2757: gate on `deadEnd`, not `target === "none"` — an empty list
    // also resolves to `none` and reported once per page load for every user.
    if (!approvalsBadge.deadEnd) {
      // Reset, so a later recurrence still reports rather than being
      // swallowed by a stale key from earlier in this page's life.
      reportedDeadEndKey.current = null;
      return;
    }
    const key = approvals.map((a) => a.id).sort().join(",");
    if (reportedDeadEndKey.current === key) return;
    reportedDeadEndKey.current = key;
    Sentry.captureMessage("pending approvals with no answerable surface", {
      level: "warning",
      tags: { ticket: "FRONT-1323", inspector_enabled: String(inspectorEnabled) },
      extra: {
        count: approvals.length,
        approval_ids: approvals.map((a) => a.id),
        types: approvals.map((a) => a.type),
      },
    });
  }, [approvalsBadge.deadEnd, approvals, inspectorEnabled]);

  function openPendingApprovals() {
    if (approvalsBadge.target === "inspector") {
      setInspectorOpen(true);
      return;
    }
    if (!approvalsBadge.chatOrigin) return;
    // Same one-modal-owner route the New chat button takes (see its note above):
    // navigate Home, then let Home's single launcher open the thread.
    if (!isHomeRoute(location.pathname)) navigate("/");
    requestOpenChat(approvalsBadge.chatOrigin, null);
  }

  function openPalette() {
    window.dispatchEvent(
      new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }),
    );
  }

  function signOut() {
    fetch("/api/auth/logout", { method: "POST" }).then(() => {
      window.location.href = "/login";
    });
  }

  // Stable identity: ChatTopBar's window keydown effect depends on this, so
  // a plain function would re-subscribe the listener on every header render.
  //
  // FRONT-1550: carries the origin path along on the open request itself
  // (`originToReturnTo` — null exactly when already home) so closing the
  // modal comes back here instead of stranding the user on Home.
  const startNewChat = useCallback(() => {
    const asset = newChatAsset();
    const returnTo = originToReturnTo(location);
    // FRONT-1938: no explicit "close Settings" call needed here — Settings
    // and Chat live behind different routes, so `navigate("/")` below already
    // unmounts Settings (and everything it held) before Home's launcher ever
    // mounts to consume this request. An earlier version tracked an `isOpen`
    // flag for this direction and called it defensively here; nothing ever
    // read the flag (review finding, PR #1379), so it was dropped along with
    // the store.
    if (returnTo !== null) navigate("/");
    requestOpenChat(asset.sessionId, null, returnTo);
  }, [location, navigate, requestOpenChat]);

  useDesktopComposerHost((session) => {
    const returnTo = originToReturnTo(location);
    if (returnTo !== null) navigate("/");
    requestOpenChat(session, null, returnTo);
  });

  // 2026-08-14 new-chatbox-topbar: opens an EXISTING chat (a board row, the
  // ask chip) from any page — same navigate-home-if-needed + requestOpenChat
  // mechanism as `startNewChat` above, just carrying a real session id/title
  // instead of a freshly minted blank one. `useChatOpenStore`'s request
  // already supports an arbitrary session id (it was never blank-only), so
  // no store changes were needed.
  const openExistingChat = useCallback(
    (sessionId: string, title: string | null) => {
      const returnTo = originToReturnTo(location);
      if (returnTo !== null) navigate("/");
      requestOpenChat(sessionId, title, returnTo);
    },
    [location, navigate, requestOpenChat],
  );

  const inspectorTooltip = getInspectorTooltip({
    chatsNeedYou,
    inspectorActive,
    activeTaskCount,
  });
  let inspectorIconColorClass: string;
  if (inspectorOpen) {
    inspectorIconColorClass = "text-primary";
  } else if (inspectorActive) {
    inspectorIconColorClass = "text-amber-600 dark:text-amber-400";
  } else {
    inspectorIconColorClass = "text-muted-foreground hover:text-foreground";
  }

  return (
    <header
      // The header's bottom edge must line up EXACTLY with the sidebar's rule
      // — the one under its workspace block, above the search box. That block
      // is `min-h-[60px]` (a fixed pixel height, its 58.5px of content never
      // reaches it); keep the two literals in step if either moves.
      //
      // 61, not 60, because of the framed shell: both regions start at the
      // same y, but the sidebar's rule sits INSIDE its card, so the card's own
      // 1px top border pushes that rule down to 61. This header is border-box,
      // so its bottom edge IS its total height — at 60 the two lines would
      // meet 1px apart across the gutter, which is visible where they nearly
      // touch.
      //
      // Framed shell: full hairline + radius, no bottom-only rule — the gap
      // under the header is canvas now, so a border-bottom would double up.
      className="h-[61px] rounded-[var(--frame-radius)] border border-border bg-card shadow-[var(--shadow-sm)] flex items-center px-2 sm:px-4 gap-1 sm:gap-2 shrink-0"
    >
      <ChatTopBar
        onNewChat={startNewChat}
        onOpenChat={openExistingChat}
        onOpenAllChats={() => navigate("/chats")}
      />

      <NotificationBell />

      {approvalsBadge.visible && (
        <Button
          onClick={openPendingApprovals}
          disabled={approvalsBadge.target === "none"}
          variant="ghost"
          size="sm"
          className={`h-8 gap-1.5 text-sm font-medium relative ${
            inspectorPanelOpen
              ? "text-primary"
              : "text-amber-600 dark:text-amber-400"
          }`}
          // FRONT-1273: explicit aria-label, not just the tooltip text — the
          // FRONT-1192 title-to-tooltip migration left this button with no
          // stable accessible name (the visible count alone reads as just a
          // number to a screen reader), which also broke the E2E locator
          // that used to key off the old native title attribute.
          aria-label={approvalsBadge.hint}
          tooltip={approvalsBadge.hint}
        >
          <ShieldCheckIcon size={16} weight="fill" />
          {approvalsBadge.count}
          {!inspectorPanelOpen && (
            <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          )}
        </Button>
      )}

      {/*
       * Inspector stays a single icon button rather than a labeled chip — the
       * pulsing amber dot is the actual signal here ("background work
       * happening, take a look"). The palette's "Panels" group (which used to
       * carry a labeled "Toggle Inspector" entry) was removed in FRONT-874, so
       * this button is now the ONLY way in — which is why it carries its own
       * `aria-label`: the tooltip text is dynamic (task counts) and can never
       * serve as a stable accessible name, and per the tooltip rule in
       * CLAUDE.md an icon-only button always needs one of its own.
       */}
      {inspectorEnabled && (
        <Button
          onClick={toggleInspector}
          variant="ghost"
          size="icon"
          // FRONT-2484: the Inspector is a side panel with no room beside a
          // phone-width page, so its toggle steps out of the crowded bar there.
          className={`h-8 w-8 relative max-sm:hidden ${inspectorIconColorClass}`}
          tooltip={inspectorTooltip}
          aria-label="Inspector"
          aria-pressed={inspectorOpen}
        >
          <SidebarSimpleIcon
            size={16}
            weight={inspectorActive ? "fill" : "regular"}
          />
          {inspectorActive && !inspectorOpen && (
            <span
              className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-500 ${
                chatsNeedYou ? "animate-pulse-fast" : "animate-pulse"
              }`}
            />
          )}
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            // Round, per the handoff's avatar trigger (§5) — a person is the
            // one thing in the chrome that isn't a panel or a control, and the
            // circle is what says so. `overflow-hidden` on the button clips
            // the avatar to the same circle, so both carry it.
            className="ml-1 p-0 overflow-hidden rounded-full"
            tooltip={userName}
            aria-label={userName}
          >
            <InitialsAvatar
              name={userName}
              size="md"
              tone="primary"
              className="h-full w-full rounded-full"
            />
          </Button>
        </DropdownMenuTrigger>
        {/* FRONT-2488: the menu grows to fit the address (up to 20rem) and a
            longer one wraps — a fixed w-52 cut ordinary emails mid-domain. */}
        <DropdownMenuContent align="end" className="w-auto min-w-52 max-w-[20rem]">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="text-sm font-medium [overflow-wrap:anywhere]">{userName}</span>
            <span className="text-[length:var(--text-2xs)] text-muted-foreground [overflow-wrap:anywhere]">
              {userEmail || "Signed in"}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {/* FRONT-1263: Profile, Security and Settings were three entries
              into what is now one destination. FRONT-1818 moved that
              destination to `/settings` and renamed it. FRONT-2376 renamed
              this row again, to "User Settings" — now that "System Settings"
              sits directly below it (the eight pages that used to live in the
              left sidebar), a bare "Settings" would be ambiguous about which
              of the two it opens, which is exactly the two-names-for-one-thing
              problem the FRONT-1818 rename set out to remove in the first
              place. */}
          <DropdownMenuItem onSelect={() => openSettings()}>
            <GearIcon size={14} />
            User Settings
          </DropdownMenuItem>
          {/* FRONT-2376: the eight former sidebar pages (Policy, Tools,
              Connections, Audit log, Org Settings, Sandbox, Status,
              Diagnostics), now a modal at `/system-settings` — visible to
              every signed-in user, same as the sidebar entries it replaced
              (each section's own admin gate, where it has one, is enforced
              inside the modal, not by hiding this row). */}
          <DropdownMenuItem onSelect={() => openSystemSettings()}>
            <SlidersHorizontalIcon size={14} />
            System Settings
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={openPalette}>
            <MagnifyingGlassIcon size={14} />
            Command palette
            <kbd className="ml-auto text-[length:var(--text-2xs)] text-muted-foreground">⌘K</kbd>
          </DropdownMenuItem>
          {switchable && switchable.tenants.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[length:var(--text-2xs)] text-muted-foreground">
                Tenant
              </DropdownMenuLabel>
              {switchable.tenants.map((t) => {
                const isCurrent = t.orgId === switchable.current;
                return (
                  <DropdownMenuItem
                    key={t.orgId}
                    disabled={isCurrent}
                    className={t.enabled ? undefined : "opacity-60"}
                    onSelect={() => {
                      // Not-enabled orgs get an explanation instead of a
                      // doomed request (the backend would 403 anyway).
                      if (!t.enabled) {
                        toast.error(`EDDY is not enabled for ${t.label} yet.`);
                        return;
                      }
                      void switchTenant(t.orgId);
                    }}
                  >
                    <BuildingsIcon size={14} />
                    <span className="truncate">{t.label}</span>
                    {isCurrent ? (
                      <CheckIcon size={12} className="ml-auto text-muted-foreground" />
                    ) : t.enabled ? null : (
                      <ProhibitIcon size={12} className="ml-auto text-muted-foreground" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={signOut} variant="destructive">
            <SignOutIcon size={14} />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
